import type {CustomerHostApi} from '@fitro/customer-features/host-contract'
import {ApiError, ApiRequestOptions, buildHeaders, handleError, parseResponse} from '@fitro/customer-features/services/api-helpers'
import {notifySessionExpired} from '@fitro/customer-features/lib/session-expired'

const CORRELATION_COOKIE = 'fitro_correlation_id'

const publicBaseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/$/, '')
const internalBaseUrl = (process.env.API_INTERNAL_BASE_URL || '').replace(/\/$/, '')
const baseUrl = (typeof window === 'undefined' && internalBaseUrl) ? internalBaseUrl : publicBaseUrl

function isUuid(value: string): boolean {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value)
}

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const prefix = `${encodeURIComponent(name)}=`
    const parts = document.cookie.split(';')
    for (const part of parts) {
        const trimmed = part.trim()
        if (trimmed.startsWith(prefix)) {
            const value = trimmed.slice(prefix.length)
            return value ? decodeURIComponent(value) : null
        }
    }
    return null
}

function ensureCorrelationId(): string {
    const existing = readCookie(CORRELATION_COOKIE)
    if (existing && isUuid(existing)) {
        return existing
    }

    const created = crypto.randomUUID()
    if (typeof document !== 'undefined') {
        const secure = window.location.protocol === 'https:'
        const cookie = `${encodeURIComponent(CORRELATION_COOKIE)}=${encodeURIComponent(created)}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
        document.cookie = cookie
    }
    return created
}

export const apiBaseUrl = baseUrl

type TokenLookupResult = {
    offline: boolean
    token: string | null
}

function isClerkOfflineError(error: unknown): boolean {
    return error instanceof Error && error.name === 'ClerkOfflineError'
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function toNumberOrUndefined(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function toBooleanOrUndefined(value: unknown): boolean | undefined {
    return typeof value === 'boolean' ? value : undefined
}

function normalizeSpringPagePayload<T>(payload: T): T {
    if (!isObjectRecord(payload)) return payload
    const data = payload as Record<string, unknown>
    const nestedPage = isObjectRecord(data.page) ? data.page : null
    const content = Array.isArray(data.content) ? data.content : null
    if (!nestedPage || !content) return payload

    const size = toNumberOrUndefined(data.size) ?? toNumberOrUndefined(nestedPage.size)
    const number = toNumberOrUndefined(data.number) ?? toNumberOrUndefined(nestedPage.number)
    const totalElements =
        toNumberOrUndefined(data.totalElements) ?? toNumberOrUndefined(nestedPage.totalElements)
    const totalPages = toNumberOrUndefined(data.totalPages) ?? toNumberOrUndefined(nestedPage.totalPages)

    const hasNestedPageMetadata =
        size !== undefined || number !== undefined || totalElements !== undefined || totalPages !== undefined
    if (!hasNestedPageMetadata) return payload

    const normalized: Record<string, unknown> = {...data}
    if (size !== undefined) normalized.size = size
    if (number !== undefined) normalized.number = number
    if (totalElements !== undefined) normalized.totalElements = totalElements
    if (totalPages !== undefined) normalized.totalPages = totalPages
    if (toNumberOrUndefined(normalized.numberOfElements) === undefined) normalized.numberOfElements = content.length
    if (toBooleanOrUndefined(normalized.empty) === undefined) normalized.empty = content.length === 0
    if (toBooleanOrUndefined(normalized.first) === undefined && number !== undefined) normalized.first = number <= 0
    if (toBooleanOrUndefined(normalized.last) === undefined && number !== undefined && totalPages !== undefined) {
        normalized.last = number + 1 >= totalPages
    }
    return normalized as T
}

async function doFetch(path: string, token: string | null, options: ApiRequestOptions) {
    const url = `${baseUrl}${path}`
    const ids = {correlationId: ensureCorrelationId(), requestId: crypto.randomUUID()}
    return fetch(url, {
        ...options,
        // Do not rely on cookies; always prefer explicit bearer token.
        credentials: options.credentials ?? 'omit',
        headers: buildHeaders(token, options.headers, ids)
    })
}

export async function apiFetch<TResponse>(
    path: string,
    options: ApiRequestOptions = {},
    _retrying = false
): Promise<TResponse> {
    const tokenResult = typeof window !== 'undefined' ? await getClerkTokenResult() : {offline: false, token: null}
    if (tokenResult.offline) {
        throw new ApiError(503, 'Authentication provider is temporarily offline')
    }

    const response = await doFetch(path, tokenResult.token, options)

    // If unauthorized, try once with a fresh token.
    if (response.status === 401 && !_retrying && typeof window !== 'undefined') {
        const refreshedTokenResult = await getClerkTokenResult(true)
        if (refreshedTokenResult.offline) {
            throw new ApiError(503, 'Authentication provider is temporarily offline')
        }
        if (refreshedTokenResult.token) {
            const retryResponse = await doFetch(path, refreshedTokenResult.token, options)
            if (retryResponse.ok) {
                const {body} = await parseResponse(retryResponse)
                return normalizeSpringPagePayload(body as TResponse)
            }
            if (retryResponse.status === 401) {
                notifySessionExpired()
            }
            return handleError<TResponse>(retryResponse)
        }
        notifySessionExpired()
    }

    if (!response.ok) {
        return handleError<TResponse>(response)
    }

    const {body} = await parseResponse(response)
    return normalizeSpringPagePayload(body as TResponse)
}

async function getClerkTokenResult(forceRefresh = false): Promise<TokenLookupResult> {
    // Client-side only: rely on Clerk JS / Next helper to fetch a session JWT
    if (typeof window === 'undefined') return {offline: false, token: null}

    const templateEnv = process.env.NEXT_PUBLIC_CLERK_TOKEN_TEMPLATE?.trim()
    const template = templateEnv ? templateEnv : undefined

    try {
        const {getToken} = await import('@/lib/clerk-nextjs')
        const token = await getToken({
            ...(template ? {template} : {}),
            ...(forceRefresh ? {skipCache: true} : {})
        })
        return {offline: false, token}
    } catch (error) {
        if (isClerkOfflineError(error)) {
            return {offline: true, token: null}
        }
        return {offline: false, token: null}
    }
}

export function resolvePublicApiBaseUrl(): string | null {
    const internalBaseUrl = (process.env.API_INTERNAL_BASE_URL || '').trim().replace(/\/$/, '')
    const publicBaseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL || '').trim().replace(/\/$/, '')
    const baseUrl = typeof window === 'undefined' ? internalBaseUrl || publicBaseUrl : publicBaseUrl
    return baseUrl || null
}

export const publicFetch: typeof fetch = (input, init) => fetch(input, init)

;({apiFetch, apiBaseUrl, publicFetch, resolvePublicApiBaseUrl}) satisfies CustomerHostApi
